import { useQueryInterruptedSessions } from '@/app-shell/chronicle/hooks/query/useQueryInterruptedSessions';
import { chronicleSessionIdAtom } from '@/atoms/chronicle';
import { useAtomValue } from 'jotai';

/**
 * Returns true when `sessionId` belongs to a Chronicle (Remote) session that
 * is awaiting bulk-sync recovery from the device — i.e., it has a
 * `live_recording` row at `status = 'session_created'` in Rust's
 * `sync_tracking` table.
 *
 * Source of truth is `useQueryInterruptedSessions` (TanStack Query against
 * the `list_interrupted_sessions` Tauri command), so this remains correct
 * across toast dismissals, BLE reconnects, and app restarts. Outside the
 * Tauri runtime (web app) the underlying query is disabled and the set is
 * empty, so this hook always returns `false` — making it safe to call
 * unconditionally from cross-platform components.
 *
 * The currently active live recording is excluded — its `live_recording` row
 * exists too, but the resume control is already disabled by other paths
 * during active recording, and showing "Recovering" while a session is
 * actively recording would be misleading.
 */
export function useIsInterruptedRemoteSession(
  sessionId: string | null | undefined
): boolean {
  const { interruptedSet } = useQueryInterruptedSessions();
  const activeChronicleSessionId = useAtomValue(chronicleSessionIdAtom);

  if (!sessionId) return false;
  if (sessionId === activeChronicleSessionId) return false;
  return interruptedSet.has(sessionId);
}
